import { useEffect, useState, useCallback } from 'react';

import { supabase } from '../../shared/services/supabaseService';

/**
 * useFamilyData - fetches the current user's family, members, and invitations from Supabase
 */
export function useFamilyData(userId: string | null) {
  const [families, setFamilies] = useState<any[]>([]);
  const [membersByFamily, setMembersByFamily] = useState<{ [familyId: string]: any[] }>({});
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFamilyData = useCallback(async () => {
    if (!userId) {
      setFamilies([]);
      setMembersByFamily({});
      setInvitations([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch all family memberships for the user
      const { data: memberships, error: membershipsError } = await supabase
        .from('family_members')
        .select('*, families(*), user_profiles(*)')
        .eq('user_id', userId);
      if (membershipsError) throw membershipsError;
      const userFamilies = (memberships || []).map((m: any) => m.families).filter(Boolean);
      // For each family, include its family_id (short code) directly from the families table
      const familiesWithCode = userFamilies.map((fam: any) => ({
        ...fam,
        family_id: fam.family_id // This is the new short code
      }));
      setFamilies(familiesWithCode);

      // 2. For each family, fetch its members
      const membersByFam: { [familyId: string]: any[] } = {};
      for (const fam of userFamilies) {
        const { data: famMembers, error: famMembersError } = await supabase
          .from('family_members')
          .select('*, user_profiles:user_profiles!inner(*)')
          .eq('family_id', fam.id);
        if (famMembersError) throw famMembersError;
        membersByFam[fam.id] = famMembers || [];
      }
      setMembersByFamily(membersByFam);

      // 3. Fetch invitations
      const { data: invitationData, error: invitationError } = await supabase
        .rpc('get_pending_invitations_for_user', {
          uid: userId,
        });
      if (invitationError) {
        setInvitations([]);
        console.error('Error loading invitations', invitationError);
      } else {
        setInvitations(invitationData || []);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load family data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchFamilyData();
    // Optionally, add realtime subscription here
  }, [fetchFamilyData]);

  return { families, membersByFamily, invitations, loading, error, refetch: fetchFamilyData };
}
